import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { ChevronDown, ChevronUp, Shield, FileText } from 'lucide-react';

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border/40 mb-2" data-testid={`section-${title.slice(0,20)}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary/30 transition-colors">
        <h2 className="font-heading text-base font-bold text-white">{title}</h2>
        {open ? <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
      </button>
      {open && <div className="px-4 pb-4 text-sm text-foreground/80 leading-relaxed space-y-3">{children}</div>}
    </div>
  );
}

export default function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="privacy-page">
      <SEO title="Polityka prywatności" description="Polityka prywatności serwisu Znajdownik.pl. Informacje o przetwarzaniu danych osobowych, plikach cookies i prawach użytkowników." />

      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">POLITYKA PRYWATNOŚCI</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Serwis Znajdownik.pl | Ostatnia aktualizacja: marzec 2026</p>

      <Section title="Wstęp" defaultOpen={true}>
        <p>Dla Właściciela serwisu Znajdownik.pl ochrona danych osobowych Użytkowników ma najwyższy priorytet. Dokładamy wszelkich starań, aby Użytkownicy czuli się bezpiecznie, powierzając nam swoje dane osobowe podczas korzystania z serwisu.</p>
        <p><strong>Użytkownik</strong> – osoba fizyczna, osoba prawna lub jednostka organizacyjna nieposiadająca osobowości prawnej, korzystająca z usług świadczonych drogą elektroniczną za pośrednictwem serwisu Znajdownik.pl.</p>
        <p>Niniejsza Polityka prywatności wyjaśnia zasady i zakres przetwarzania danych osobowych, prawa przysługujące Użytkownikom, obowiązki administratora danych oraz informuje o wykorzystywaniu plików cookies.</p>
        <p>Administrator stosuje najnowocześniejsze środki techniczne i organizacyjne, zapewniające wysoki poziom ochrony przetwarzanych danych osobowych oraz zabezpieczenie przed dostępem osób nieupoważnionych.</p>
      </Section>

      <Section title="I. Administrator danych osobowych">
        <p>Administratorem danych osobowych jest właściciel serwisu Znajdownik.pl z siedzibą w Polsce (dalej zwany „Administratorem" lub „Właścicielem").</p>
        <p>Kontakt w sprawach ochrony danych osobowych: <a href="mailto:kontakt@znajdownik.pl" className="text-primary hover:underline">kontakt@znajdownik.pl</a></p>
      </Section>

      <Section title="II. Cele przetwarzania danych osobowych">
        <p>Administrator przetwarza dane osobowe Użytkownika w celu prawidłowego świadczenia usług za pośrednictwem serwisu Znajdownik.pl. Dane są niezbędne w szczególności do:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>zarejestrowania się w serwisie (nick, e-mail, telefon),</li>
          <li>korzystania z funkcji społecznościowych (komentarze, reakcje, grupy),</li>
          <li>korzystania z systemu walutowego Zorki,</li>
          <li>korzystania z funkcji AI (Wąchalski, generowanie opisów),</li>
          <li>wysyłania zaproszeń i powiadomień,</li>
          <li>prowadzenia statystyk i analiz użytkowania serwisu.</li>
        </ul>
        <p>Za odrębną zgodą Użytkownika dane mogą być przetwarzane w celu przesyłania informacji handlowych o nowościach, promocjach i ofertach.</p>
      </Section>

      <Section title="III. Rodzaj przetwarzanych danych osobowych">
        <p><strong>Dane niezbędne przy rejestracji:</strong> nick (pseudonim), opcjonalnie adres e-mail lub numer telefonu.</p>
        <p><strong>Dane opcjonalne:</strong> zdjęcie profilowe, dane podane w profilu użytkownika.</p>
        <p><strong>Dane zbierane automatycznie:</strong> adres IP, typ przeglądarki, czas wizyty, przeglądane strony, dane z plików cookies.</p>
      </Section>

      <Section title="IV. Podstawa prawna przetwarzania">
        <p>Przetwarzanie danych odbywa się zgodnie z Rozporządzeniem Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. (RODO), w szczególności na podstawie:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>art. 6 ust. 1 lit. b RODO</strong> – wykonanie umowy (świadczenie usług serwisu),</li>
          <li><strong>art. 6 ust. 1 lit. c RODO</strong> – wypełnienie obowiązków prawnych,</li>
          <li><strong>art. 6 ust. 1 lit. f RODO</strong> – prawnie uzasadnione interesy Administratora (analityka, bezpieczeństwo),</li>
          <li><strong>art. 6 ust. 1 lit. a RODO</strong> – zgoda Użytkownika (np. newsletter, marketing).</li>
        </ul>
      </Section>

      <Section title="V. Prawa osoby, której dane dotyczą">
        <p>Zgodnie z RODO przysługują Państwu następujące prawa:</p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Prawo dostępu do danych oraz otrzymania ich kopii.</li>
          <li>Prawo do sprostowania (poprawienia) danych.</li>
          <li>Prawo do usunięcia danych („prawo do bycia zapomnianym").</li>
          <li>Prawo do ograniczenia przetwarzania.</li>
          <li>Prawo do przenoszenia danych.</li>
          <li>Prawo do wniesienia sprzeciwu wobec przetwarzania.</li>
          <li>Prawo do wycofania zgody w dowolnym momencie.</li>
          <li>Prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych.</li>
        </ol>
        <p>Żądania można kierować na adres: <a href="mailto:kontakt@znajdownik.pl" className="text-primary hover:underline">kontakt@znajdownik.pl</a>. Administrator udzieli odpowiedzi w terminie do 1 miesiąca.</p>
      </Section>

      <Section title="VI. Okres przechowywania danych">
        <p>Dane przechowywane są przez okres niezbędny do realizacji celów przetwarzania. Dane konta użytkownika – do czasu usunięcia konta. Dane marketingowe – do czasu wycofania zgody. Po ustaniu celu przetwarzania dane są usuwane lub anonimizowane.</p>
      </Section>

      <Section title="VII. Odbiorcy danych">
        <p>Dane mogą być powierzone podmiotom współpracującym wyłącznie w zakresie niezbędnym do realizacji usług:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>dostawcom usług hostingowych i serwerowych,</li>
          <li>dostawcom usług analitycznych (Google Analytics),</li>
          <li>dostawcom usług reklamowych (Google AdSense),</li>
          <li>dostawcom usług AI (xAI/Grok).</li>
        </ul>
        <p>Dane nie są przekazywane podmiotom trzecim w celach marketingowych bez odrębnej zgody.</p>
      </Section>

      <Section title="VIII. Pliki cookies">
        <p>Serwis wykorzystuje pliki cookies oraz podobne technologie w celach statystycznych, reklamowych oraz zapewnienia prawidłowego funkcjonowania serwisu.</p>
        <p>Stosowane cookies:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Niezbędne</strong> – zapewniają podstawowe funkcje (sesja, logowanie),</li>
          <li><strong>Analityczne</strong> – Google Analytics (G-NNL9ZQJH7T) – statystyki ruchu,</li>
          <li><strong>Reklamowe</strong> – Google AdSense (ca-pub-8782302140557139) – personalizacja reklam,</li>
          <li><strong>Funkcjonalne</strong> – preferencje użytkownika, PWA.</li>
        </ul>
        <p>Użytkownik może w dowolnym momencie zmienić ustawienia cookies w swojej przeglądarce. Blokada niektórych cookies może ograniczyć funkcjonalność serwisu.</p>
      </Section>

      <Section title="IX. Linki afiliacyjne">
        <p>Serwis Znajdownik.pl zawiera linki afiliacyjne do produktów i usług zewnętrznych sprzedawców. Kliknięcie w taki link i dokonanie zakupu może generować prowizję dla Znajdownik.pl. Nie wpływa to na cenę produktu dla Użytkownika. Produkty prezentowane w serwisie nie stanowią oferty handlowej w rozumieniu Kodeksu Cywilnego.</p>
      </Section>

      <Section title="X. Zmiany polityki prywatności">
        <p>Niniejsza Polityka prywatności może ulegać zmianom. O istotnych zmianach poinformujemy z odpowiednim wyprzedzeniem poprzez komunikat w serwisie.</p>
      </Section>

      <p className="text-xs text-muted-foreground mt-6">Kontakt: <a href="mailto:kontakt@znajdownik.pl" className="text-primary">kontakt@znajdownik.pl</a> | Wykonanie: <a href="https://www.zens.pl" className="text-primary" target="_blank" rel="noopener noreferrer">Zens IT</a></p>
    </div>
  );
}
